'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import React, { Suspense, useRef, useState } from 'react'

import { Input } from '@/app/components/Form/Input'
import { Icon } from '@/app/components/Icon'

export type SearchableAreaProps<T> = {
  items: T[]
  searchFn: (items: T[], query: string) => T[]
  renderItems: (items: T[]) => React.ReactNode
  noResultsFn: (query: string) => React.ReactNode
  searchPlaceholder?: string
}

function SearchableAreaInner<T>({
  items,
  searchFn,
  renderItems,
  noResultsFn,
  searchPlaceholder,
}: SearchableAreaProps<T>) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const initialQ = searchParams.get('q') || ''
  const [query, setQuery] = useState(initialQ)
  const [submittedQuery, setSubmittedQuery] = useState(initialQ)

  const filtered = submittedQuery ? searchFn(items, submittedQuery) : items

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const trimmed = query.trim()
    setSubmittedQuery(trimmed)
    const params = new URLSearchParams(searchParams.toString())
    if (trimmed) {
      params.set('q', trimmed)
    } else {
      params.delete('q')
    }
    router.push(`?${params.toString()}`, { scroll: false })
    containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  function handleClear() {
    setQuery('')
    if (submittedQuery) {
      setSubmittedQuery('')
      const params = new URLSearchParams(searchParams.toString())
      params.delete('q')
      router.push(`?${params.toString()}`, { scroll: false })
    }
    inputRef.current?.focus()
  }

  return (
    <div ref={containerRef}>
      <form onSubmit={handleSubmit} role="search" className="relative">
        <button
          type="submit"
          className="absolute cursor-pointer z-2 left-2xl top-lg flex items-center justify-center w-[18px] h-[18px] lg:top-xl lg:w-[22px] lg:h-[22px] lg:[&_svg]:!h-[20px] lg:[&_svg]:!w-[20px]"
        >
          <Icon
            provider="fontawesome"
            name="faSearch"
            style="solid"
            size={15}
            className="text-neutral-600"
          />
        </button>
        {query && (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Clear search"
            className="absolute cursor-pointer z-2 right-2xl top-lg flex items-center justify-center w-[18px] h-[18px] lg:top-xl lg:w-[22px] lg:h-[22px] lg:[&_svg]:!h-[20px] lg:[&_svg]:!w-[20px]"
          >
            <Icon
              provider="fontawesome"
              name="faClose"
              style="solid"
              size={15}
              className="text-neutral-600"
            />
          </button>
        )}
        <Input
          ref={inputRef}
          type="search"
          placeholder={searchPlaceholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="[&::-webkit-search-cancel-button]:hidden !px-[50px] lg:!px-[54px]"
        />
      </form>
      <div>
        {filtered.length > 0
          ? renderItems(filtered)
          : submittedQuery
            ? noResultsFn(submittedQuery)
            : renderItems(items)}
      </div>
    </div>
  )
}

export function SearchableArea<T>(props: SearchableAreaProps<T>) {
  return (
    <Suspense fallback={null}>
      <SearchableAreaInner {...props} />
    </Suspense>
  )
}
